import React, { useCallback, useMemo, useRef, useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { View, Dimensions, Platform, Text } from 'react-native';
import { useSelector } from 'react-redux';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import BaseHierarchy, { BaseHierarchyHandle, HierarchyItem, HierarchyPosition, HierarchyLink } from '@components/Hierarchy/BaseHierarchy';
import DraggableUserCard, { DraggableUserCardHandle } from '@components/Hierarchy/DraggableUserCard';
import UserCard from './UserCard';
import ArrowView from '../utils/ArrowView';
import DepartmentGrouping, { DepartmentLegend } from './DepartmentGrouping';
import { groupUsersByDepartment } from './TestData';
import { calculateOrganizedPositions } from '../utils/departmentPositioning';

// Enhanced User interfaces
interface UserProfile {
  name?: string;
  surname?: string;
  avatar?: any;
}

interface User extends HierarchyItem {
  _id: string;
  email?: string;
  profile_information?: UserProfile;
  department?: string;
  role?: string;
  status?: string;
}

interface Department {
  _id: string;
  name: string;
  users?: User[];
}

interface UserPosition {
  x: number;
  y: number;
}

// Enhanced UserHierarchy interface for imperative control
export interface OptimizedUserHierarchyHandle {
  setComponentPosition: (id: string, x: number, y: number) => void;
  getComponentPosition: (id: string) => { x: number, y: number } | null;
  centerAllComponents: () => void;
  centerView: () => void;
  moveTo: (x: number, y: number) => void;
  zoomTo: (zoom: number, zoomCenter: { x: number, y: number }) => void;
  zoomBy: (delta: number) => void;
  getCurrentZoom: () => number;
  getViewportSize: () => { width: number, height: number };
  forceUpdate: () => void;
}

interface OptimizedUserHierarchyProps {
  userList: User[];
  loadingError?: any;
  departmentList?: Department[];
  uniqueDepartmentList?: any;
  openAddUserModal: () => void;
  openAddToDepartmentModal: (department: Department) => void;
  handleSnap: (target: any, source: any) => void;
  owner: any;
  linkList: any[];
  companyId: string;
  isLoading?: boolean;
  refresh: () => void;
}

// Optimized Connection Lines Component using ArrowView
const ConnectionLines = ({ connections, userPositions, theme, zoomLevel }) => {
  // Disable arrows on Android to prevent bitmap size crashes
  if (Platform.OS === 'android') return null;

  if (!connections || connections.length === 0) return null;

  const getLineColor = (connectionType: string) => {
    switch (connectionType) {
      case 'reports_to': return theme ? '#4CAF50' : '#66BB6A';
      case 'manages': return theme ? '#2196F3' : '#42A5F5';
      case 'collaborates': return theme ? '#FF9800' : '#FFA726';
      default: return theme ? '#9E9E9E' : '#BDBDBD';
    }
  };

  return (
    <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }} pointerEvents="none">
      {connections.map((connection, index) => {
        const fromPos = userPositions[connection.from];
        const toPos = userPositions[connection.to];

        if (!fromPos || !toPos) return null;

        const fromX = fromPos.x + 100; // Center of card
        const fromY = fromPos.y + 60;
        const toX = toPos.x + 100;
        const toY = toPos.y + 60;

        return (
          <ArrowView
            key={`${connection.from}-${connection.to}-${index}`}
            from={{ x: fromX, y: fromY }}
            to={{ x: toX, y: toY }}
            color={getLineColor(connection.type)}
            width={connection.type === 'manages' ? 3 : 2}
            dash={connection.type === 'collaborates' ? [5, 5] : []}
            animate={false}
            pressCallback={() => { }}
            data={connection}
            showAvs={false}
            text=""
          />
        );
      })}
    </View>
  );
};

// Main Optimized UserHierarchy Component
const OptimizedUserHierarchy = forwardRef<OptimizedUserHierarchyHandle, OptimizedUserHierarchyProps>((props, ref) => {
  const {
    userList = [],
    owner,
    departmentList = [],
    isLoading = false,
    linkList = [],
    refresh,
    handleSnap,
    openAddUserModal,
    openAddToDepartmentModal
  } = props;

  const theme = themeSelector();
  const baseHierarchyRef = useRef<BaseHierarchyHandle>(null);
  const draggableUserRefs = useRef<Map<string, DraggableUserCardHandle>>(new Map());

  // Convert linkList to HierarchyLink format with optimistic link support
  const hierarchyLinks: HierarchyLink[] = useMemo(() => {
    return (linkList || []).map(link => ({
      sourceId: link.from,
      targetId: link.to,
      type: link.type || 'connection',
      color: link.isOptimistic
        ? '#f59e0b' // Amber for optimistic links
        : link.type === 'manages'
          ? '#2196F3'
          : link.type === 'reports_to'
            ? '#4CAF50'
            : '#FF9800',
      width: link.isOptimistic ? 2 : (link.type === 'manages' ? 3 : 2),
      dashArray: link.isOptimistic ? [8, 4] : undefined, // Dashed line for optimistic links
      opacity: link.isOptimistic ? 0.7 : 1
    }));
  }, [linkList]);

  // Prepare active users only (excluding pending users)
  const allUsers: User[] = useMemo(() => {
    console.log('🔍 OptimizedUserHierarchy owner data:', {
      owner,
      ownerId: owner?._id,
      ownerEmail: owner?.email,
      ownerProfile: owner?.profile_information,
      hasOwner: !!owner,
      userListLength: userList?.length
    });

    // Filter out pending users - they should be shown in the pending panel, not as draggable cards
    // Allow agents through, or filter out pending users
    const activeUsers = userList.filter(user =>
      // Allow agents through
      ((user as any).isAgent || (user as any).type === 'agent') ||
      // Or filter out pending users
      (!(user as any).isPending && (user as any).status !== 'pending')
    );

    // Always include owner as the main draggable card
    if (owner && owner._id) {
      activeUsers.unshift(owner);
    } else {
      // Create a fallback current user if no owner data
      const fallbackUser = {
        _id: 'current-user',
        email: 'current.user@company.com',
        profile_information: {
          name: 'Current',
          surname: 'User',
          avatar: null
        },
        role: 'Owner',
        department: 'Management',
        status: 'active'
      };
      activeUsers.unshift(fallbackUser);
    }

    return activeUsers;
  }, [userList, owner]);

  // Group users by department for visual organization
  const departmentGroups = useMemo(() => {
    return groupUsersByDepartment(allUsers);
  }, [allUsers]);

  // Set initial organized positions after component mounts
  useEffect(() => {
    if (allUsers.length > 0 && baseHierarchyRef.current) {
      // Use setTimeout to ensure component is fully mounted
      setTimeout(() => {
        const organizedPositions = calculateOrganizedPositions(allUsers, owner);

        // Apply positions to each user
        Object.entries(organizedPositions).forEach(([userId, position]) => {
          baseHierarchyRef.current?.setComponentPosition(userId, position.x, position.y);
        });

        console.log('🎯 Applied organized positions:', organizedPositions);

        // Force a re-render to show the new positions
        baseHierarchyRef.current?.forceUpdate();
      }, 100);
    }
  }, [allUsers, owner]);

  // Render individual user card using the specialized user card with arrow functionality
  const renderUser = useCallback((user: User, position: HierarchyPosition, onPositionChange: (position: HierarchyPosition) => void, zoomLevel?: number) => {
    console.log('🎯 OptimizedUserHierarchy renderUser called:', {
      userId: user._id,
      position,
      zoomLevel
    });

    return (
      <DraggableUserCard
        ref={(ref) => {
          if (ref) {
            draggableUserRefs.current.set(user._id, ref);
          } else {
            draggableUserRefs.current.delete(user._id);
          }
        }}
        user={user}
        initialPosition={position}
        onPositionChange={(itemId, newPosition) => onPositionChange(newPosition)}
        onPositionUpdate={(itemId, newPosition) => onPositionChange(newPosition)}
        onSnap={handleSnap}
        getAllUserPositions={() => {
          // Get all user positions for arrow snapping
          const positions: Record<string, HierarchyPosition> = {};
          allUsers.forEach(u => {
            const ref = draggableUserRefs.current.get(u._id);
            if (ref) {
              positions[u._id] = ref.getPosition();
            }
          });
          return positions;
        }}
        arrowHandler={({ id, path, reset }) => {
          // Handle arrow drawing feedback
          console.log('🎯 Arrow handler:', { id, path, reset });
        }}
        dragEnabled={true}
        zoomLevel={zoomLevel || 1}
        itemWidth={200}
        itemHeight={120}
        borderRadius={12}
        shadowEnabled={true}
      >
        {({ isDragging: itemDragging }) => (
          <UserCard
            user={user}
            theme={theme}
            isDragging={itemDragging}
            showArrows={true}
            portrait={true}
          />
        )}
      </DraggableUserCard>
    );
  }, [theme, handleSnap, allUsers]);

  // Render link handler with optimized connections
  const renderLinkHandler = useCallback((linkHandlerProps) => {
    return (
      <ConnectionLines
        connections={linkList || []}
        userPositions={linkHandlerProps.itemPositionsRef.current}
        theme={theme}
        zoomLevel={linkHandlerProps.zoomLevel}
      />
    );
  }, [linkList, theme]);

  // Handle position changes
  const handlePositionChange = useCallback((userId: string, position: HierarchyPosition) => {
    // BaseHierarchy handles position updates internally
    // This is just for any additional logic we might need
  }, []);

  // Expose imperative methods through ref
  useImperativeHandle(ref, () => ({
    setComponentPosition: (id: string, x: number, y: number) => {
      baseHierarchyRef.current?.setComponentPosition(id, x, y);
    },
    getComponentPosition: (id: string) => {
      return baseHierarchyRef.current?.getComponentPosition(id) || null;
    },
    centerAllComponents: () => {
      baseHierarchyRef.current?.centerAllComponents();
    },
    centerView: () => {
      baseHierarchyRef.current?.centerView();
    },
    moveTo: (x: number, y: number) => {
      baseHierarchyRef.current?.moveTo(x, y);
    },
    zoomTo: (zoom: number, zoomCenter: { x: number, y: number }) => {
      baseHierarchyRef.current?.zoomTo(zoom, zoomCenter);
    },
    zoomBy: (delta: number) => {
      baseHierarchyRef.current?.zoomBy(delta);
    },
    getCurrentZoom: () => {
      return baseHierarchyRef.current?.getCurrentZoom() || 1;
    },
    getViewportSize: () => {
      return baseHierarchyRef.current?.getViewportSize() || { width: 4000, height: 4000 };
    },
    forceUpdate: () => {
      baseHierarchyRef.current?.forceUpdate();
    }
  }), []);



  // Early return if no users to prevent rendering issues
  if (allUsers.length === 0 && !isLoading) {
    console.warn('⚠️ OptimizedUserHierarchy: No users to render');
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <Text style={{ color: theme ? '#000' : '#fff' }}>No users available</Text>
      </View>
    );
  }

  return (
    <View style={{ flex: 1 }}>
      {/* Department Legend */}
      <DepartmentLegend
        departmentGroups={departmentGroups}
        theme={theme}
        position={Platform.OS === 'android' ? 'bottom-left' : 'bottom-right'}
      />

      {/* Base Hierarchy with all functionality */}
      <BaseHierarchy
        ref={baseHierarchyRef}
        items={allUsers}
        primaryItem={owner}
        links={hierarchyLinks}
        isLoading={isLoading}
        onItemPositionChange={handlePositionChange}
        onSnap={handleSnap}
        renderItem={renderUser}
        renderLinkHandler={renderLinkHandler}
        viewportSize={Platform.OS === 'android'
          ? { width: 3000, height: 3000 }
          : { width: 4000, height: 4000 }}
        gridSize={50}
        initialZoom={0.6}
        minZoom={0.3}
        maxZoom={2}
        positioningStrategy="circular"
        centerSpacing={400}
        showGrid={false} // Disabled on all platforms, especially Android
        showDebugInfo={__DEV__}
        backgroundColor={theme ? '#f9f9f9' : '#1a1a1a'}
      />

      {/* Department Grouping Overlay */}
      <DepartmentGrouping
        departmentGroups={departmentGroups}
        userPositions={baseHierarchyRef.current?.getAllItemPositions?.() || {}}
        theme={theme}
        zoomLevel={baseHierarchyRef.current?.getCurrentZoom?.() || 1}
      />
    </View>
  );
});

OptimizedUserHierarchy.displayName = 'OptimizedUserHierarchy';

export default OptimizedUserHierarchy;
